<!-- FeedbackSection.svelte - User feedback collection component -->
<script>
    import cookieManager from '../utils/cookieManager.js';

    // Props - decisionId is passed from parent component (TopPriorityApp)
    let { appType = 'top_priority', decisionId = null } = $props();

    // State
    let selectedSentiment = $state(null); // 'positive' | 'negative' | null
    let sentimentSubmitting = $state(false);
    let showTagsSection = $state(false);
    
    let selectedTags = $state([]);
    let tagsSubmitting = $state(false);
    let tagsSubmitted = $state(false);

    // Default tags based on sentiment
    const positiveTags = [
        'Clarifying',
        'Would recommend',
        'Motivating',
        'Useful'
    ];
    
    const negativeTags = [
        'Not for me',
        'Too complicated',
        'Confusing',
        'Missing features'
    ];
    
    // Reactive: get default tags based on current sentiment
    let defaultTags = $derived(selectedSentiment === 'negative' ? negativeTags : positiveTags);

    // Custom tags added by user
    let customTags = $state([]);
    let customTagInput = $state('');
    const MAX_CUSTOM_TAGS = 4;

    // Check if running in development mode
    const isDevelopment = typeof window !== 'undefined' && 
        (window.location.port === '5174' || window.location.hostname === 'localhost');

    // Get API base URL
    const getApiUrl = (endpoint) => {
        if (isDevelopment) {
            return `http://localhost:5000${endpoint}`;
        }
        return endpoint;
    };

    // Handle thumbs up/down click
    async function handleSentimentClick(sentiment) {
        const sentimentChanged = selectedSentiment !== null && selectedSentiment !== sentiment;
        selectedSentiment = sentiment;
        showTagsSection = true;
        sentimentSubmitting = true;
        
        // Reset tags if sentiment changed (user switched from positive to negative or vice versa)
        if (sentimentChanged) {
            selectedTags = [];
            // Keep custom tags but deselect them - user can re-select if relevant
        }

        const feedbackData = {
            userId: cookieManager.userId,
            sessionId: cookieManager.sessionId,
            decisionId: decisionId,
            sentiment: sentiment,
            appType: appType
        };

        try {
            const controller = new AbortController();
            const timeoutDuration = isDevelopment ? 3000 : 10000;
            const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

            const response = await fetch(getApiUrl('/api/feedback/sentiment'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(feedbackData),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`Server error: ${response.status}`);
            }

            const result = await response.json();
            console.log('Sentiment stored:', result);

        } catch (error) {
            if (isDevelopment) {
                // Simulate success in development
                console.log('Development mode: Simulating sentiment storage', feedbackData);
            } else {
                console.error('Failed to store sentiment:', error);
            }
        } finally {
            sentimentSubmitting = false;
        }
    }

    // Toggle tag selection
    function toggleTag(tag) {
        if (selectedTags.includes(tag)) {
            selectedTags = selectedTags.filter(t => t !== tag);
        } else {
            selectedTags = [...selectedTags, tag];
        }
    }

    // Handle custom tag input - sanitize
    function handleCustomTagInput(event) {
        let value = event.target.value;
        
        // Basic client-side sanitization
        value = value.replace(/<[^>]*>/g, ''); // Strip HTML tags
        value = value.replace(/javascript:/gi, ''); // Remove javascript: protocol
        
        // Limit to 100 characters
        if (value.length > 100) {
            value = value.slice(0, 100);
        }
        
        customTagInput = value;
    }

    // Add custom tag to the list
    function addCustomTag() {
        const trimmed = customTagInput.trim();
        if (!trimmed) return;
        if (customTags.length >= MAX_CUSTOM_TAGS) return;
        
        // Check if tag already exists (in default or custom)
        const allExistingTags = [...defaultTags, ...customTags].map(t => t.toLowerCase());
        if (allExistingTags.includes(trimmed.toLowerCase())) {
            customTagInput = '';
            return;
        }
        
        // Add to custom tags and auto-select it
        customTags = [...customTags, trimmed];
        selectedTags = [...selectedTags, trimmed];
        customTagInput = '';
    }

    // Handle Enter key in custom tag input
    function handleCustomTagKeydown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            addCustomTag();
        }
    }

    // Remove a custom tag
    function removeCustomTag(tag) {
        customTags = customTags.filter(t => t !== tag);
        selectedTags = selectedTags.filter(t => t !== tag);
    }

    // Handle tags submission
    async function handleTagsSubmit() {
        // Auto-add any pending custom tag before submitting
        if (customTagInput.trim()) {
            addCustomTag();
        }
        
        if (selectedTags.length === 0) {
            return; // Nothing to submit
        }

        tagsSubmitting = true;

        // Separate default tags from custom tags for storage
        const selectedDefaultTags = selectedTags.filter(t => defaultTags.includes(t));
        const selectedCustomTags = selectedTags.filter(t => customTags.includes(t));

        const feedbackData = {
            sessionId: cookieManager.sessionId,
            decisionId: decisionId,
            tags: selectedDefaultTags,
            customTags: selectedCustomTags
        };

        try {
            const controller = new AbortController();
            const timeoutDuration = isDevelopment ? 3000 : 10000;
            const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

            const response = await fetch(getApiUrl('/api/feedback/tags'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(feedbackData),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`Server error: ${response.status}`);
            }

            const result = await response.json();
            console.log('Tags stored:', result);
            tagsSubmitted = true;

        } catch (error) {
            if (isDevelopment) {
                // Simulate success in development
                console.log('Development mode: Simulating tags storage', feedbackData);
                tagsSubmitted = true;
            } else {
                console.error('Failed to store tags:', error);
                alert('Could not submit feedback. Please try again.');
            }
        } finally {
            tagsSubmitting = false;
        }
    }
</script>

<div class="feedback-section">
    <!-- Initial Question -->
    <div class="feedback-question">
        <p class="feedback-title">What do you think of Top Priority?</p>
        
        <div class="thumbs-container">
            <button
                class="thumb-button"
                class:selected={selectedSentiment === 'positive'}
                class:unselected={selectedSentiment === 'negative'}
                onclick={() => handleSentimentClick('positive')}
                disabled={sentimentSubmitting}
                aria-label="Thumbs up"
            >
                <span class="thumb-emoji">👍</span>
            </button>
            
            <button
                class="thumb-button"
                class:selected={selectedSentiment === 'negative'}
                class:unselected={selectedSentiment === 'positive'}
                onclick={() => handleSentimentClick('negative')}
                disabled={sentimentSubmitting}
                aria-label="Thumbs down"
            >
                <span class="thumb-emoji">👎</span>
            </button>
        </div>
    </div>

    <!-- Tags Section - only shows after sentiment selection -->
    {#if showTagsSection}
        <div class="tags-section" class:submitted={tagsSubmitted}>
            {#if tagsSubmitted}
                <div class="success-state">
                    <span class="success-icon">✓</span>
                    <p>Thank you for your feedback!</p>
                </div>
            {:else}
                <p class="tags-title">Tell us more</p>
                
                <div class="tags-container">
                    {#each defaultTags as tag}
                        <button
                            class="tag-button"
                            class:selected={selectedTags.includes(tag)}
                            onclick={() => toggleTag(tag)}
                            type="button"
                        >
                            {tag}
                        </button>
                    {/each}
                    
                    {#each customTags as tag}
                        <button
                            class="tag-button custom-tag"
                            class:selected={selectedTags.includes(tag)}
                            onclick={() => toggleTag(tag)}
                            type="button"
                        >
                            {tag}
                            <span 
                                class="remove-tag" 
                                onclick={(e) => { e.stopPropagation(); removeCustomTag(tag); }}
                                role="button"
                                tabindex="0"
                                onkeydown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); removeCustomTag(tag); }}}
                            >×</span>
                        </button>
                    {/each}
                </div>

                <!-- Custom Tags Input -->
                {#if customTags.length < MAX_CUSTOM_TAGS}
                    <div class="custom-tag-input-container">
                        <input
                            type="text"
                            class="custom-tag-input"
                            placeholder="Add your own tag..."
                            value={customTagInput}
                            oninput={handleCustomTagInput}
                            onkeydown={handleCustomTagKeydown}
                            maxlength="100"
                        />
                        <button
                            type="button"
                            class="add-tag-button"
                            class:highlight={customTagInput.trim()}
                            onclick={addCustomTag}
                            disabled={!customTagInput.trim()}
                            aria-label="Add tag"
                        >
                            +
                        </button>
                    </div>
                {:else}
                    <p class="max-tags-notice">Maximum custom tags reached</p>
                {/if}

                <button
                    class="submit-button"
                    onclick={handleTagsSubmit}
                    disabled={tagsSubmitting || selectedTags.length === 0}
                >
                    {tagsSubmitting ? 'Submitting...' : 'Submit'}
                </button>
                
                <p class="submit-subtitle">
                    Help the next person prioritize better with an unfiltered review
                </p>
            {/if}
        </div>
    {/if}
</div>

<style>
    .feedback-section {
        width: 100%;
        max-width: 500px;
        margin: 2rem auto;
        padding: 1.5rem;
        text-align: center;
    }

    .feedback-question {
        margin-bottom: 1rem;
    }

    .feedback-title {
        font-size: 1rem;
        color: #555;
        margin-bottom: 1rem;
    }

    .thumbs-container {
        display: flex;
        justify-content: center;
        gap: 1.5rem;
    }

    .thumb-button {
        background: none;
        border: 2px solid transparent;
        border-radius: 50%;
        padding: 0.75rem;
        cursor: pointer;
        transition: all 0.3s ease;
        opacity: 1;
    }

    .thumb-button:hover:not(:disabled) {
        transform: scale(1.15);
    }

    .thumb-button:disabled {
        cursor: wait;
    }

    .thumb-button.selected {
        transform: scale(1.2);
        border-color: #9cfffa;
        background: rgba(156, 255, 250, 0.2);
        animation: pulse 0.3s ease;
    }

    .thumb-button.unselected {
        opacity: 0.35;
        filter: grayscale(80%);
        transform: scale(0.9);
    }

    .thumb-emoji {
        font-size: 2.8rem;
        display: block;
    }

    @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.3); }
        100% { transform: scale(1.2); }
    }

    /* Tags Section */
    .tags-section {
        margin-top: 1.5rem;
        padding-top: 1.5rem;
        border-top: 1px solid #e0e0e0;
        animation: slideDown 0.3s ease;
    }

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .tags-title {
        font-size: 1rem;
        color: #333;
        font-weight: 600;
        margin-bottom: 1rem;
    }

    .tags-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.5rem;
        margin-bottom: 1rem;
    }

    .tag-button {
        background: transparent;
        border: 1px solid #555;
        border-radius: 20px;
        padding: 0.5rem 1rem;
        font-size: 0.9rem;
        color: #555;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .tag-button:hover {
        border-color: #084b83;
        color: #084b83;
    }

    .tag-button.selected {
        background: linear-gradient(135deg, #9cfffa 0%, #7de8e3 100%);
        border-color: #7de8e3;
        color: #084b83;
        font-weight: 500;
    }

    .tag-button.custom-tag {
        position: relative;
        padding-right: 1.75rem;
    }

    .remove-tag {
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
        font-size: 1.1rem;
        font-weight: bold;
        color: #999;
        cursor: pointer;
        line-height: 1;
    }

    .remove-tag:hover {
        color: #e53935;
    }

    .custom-tag-input-container {
        display: flex;
        gap: 0.5rem;
        margin: 1rem 0;
        max-width: 300px;
        margin-left: auto;
        margin-right: auto;
    }

    .custom-tag-input {
        flex: 1;
        padding: 0.6rem 1rem;
        border: 1px solid #555;
        border-radius: 20px;
        font-size: 0.9rem;
        transition: border-color 0.2s ease;
    }

    .custom-tag-input:focus {
        outline: none;
        border-color: #9cfffa;
        box-shadow: 0 0 0 3px rgba(156, 255, 250, 0.2);
    }

    .add-tag-button {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: 1px solid #555;
        background: transparent;
        font-size: 1.75rem;
        font-weight: 300;
        color: #555;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        flex-shrink: 0;
        line-height: 0;
        padding-bottom: 2px; /* Optical adjustment for '+' centering */
    }

    .add-tag-button:hover:not(:disabled) {
        background: linear-gradient(135deg, #9cfffa 0%, #7de8e3 100%);
        border-color: #7de8e3;
        color: #084b83;
    }

    .add-tag-button:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .add-tag-button.highlight {
        background: linear-gradient(135deg, #9cfffa 0%, #7de8e3 100%);
        border-color: #7de8e3;
        color: #084b83;
        animation: pulseHighlight 1s ease-in-out infinite;
    }

    @keyframes pulseHighlight {
        0%, 100% { 
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(156, 255, 250, 0.6);
        }
        50% { 
            transform: scale(1.05);
            box-shadow: 0 0 0 8px rgba(156, 255, 250, 0);
        }
    }

    .max-tags-notice {
        font-size: 0.8rem;
        color: #888;
        margin: 1rem 0;
        font-style: italic;
    }

    .submit-button {
        background: linear-gradient(135deg, #9cfffa 0%, #7de8e3 100%);
        color: #084b83;
        border: none;
        padding: 0.75rem 2rem;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        margin-top: 0.5rem;
    }

    .submit-button:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(156, 255, 250, 0.4);
    }

    .submit-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .submit-subtitle {
        font-size: 0.8rem;
        color: #888;
        margin-top: 0.75rem;
        font-style: italic;
    }

    /* Success State */
    .success-state {
        padding: 1rem;
        animation: fadeIn 0.3s ease;
    }

    .success-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background: #4caf50;
        color: white;
        border-radius: 50%;
        font-size: 1.25rem;
        font-weight: bold;
        margin-bottom: 0.5rem;
    }

    .success-state p {
        color: #333;
        font-size: 1rem;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    /* Mobile adjustments */
    @media (max-width: 480px) {
        .feedback-section {
            padding: 1rem;
        }

        .thumb-emoji {
            font-size: 2.75rem;
        }

        .tags-container {
            gap: 0.4rem;
        }

        .tag-button {
            padding: 0.4rem 0.8rem;
            font-size: 0.85rem;
        }
    }
</style>
